/* KST Select (React 19) - Mobile Modal + Desktop Dropdown
   Lightweight alternative to KstGroupedSelect for simple lists
   Converted from Vue 2 KstSelect component
*/
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  memo,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';

export interface DropdownlistItem {
  [key: string]: any;
}

export interface DropdownlistProps {
  value: string | number | null;
  items: DropdownlistItem[];
  labelKey?: string;
  valueKey?: string;
  placeholder?: string;
  sortItems?: boolean;
  searchable?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  onChange?: (value: string | number | null) => void;
  onItemChange?: (item: DropdownlistItem | null) => void;
  onCancel?: () => void;
  onTab?: (info: { shiftKey: boolean }) => void;
  onOpen?: () => void;
  onClose?: () => void;
  defaultOpen?: boolean;
}

export const Dropdownlist = memo(function Dropdownlist({
  value,
  items = [],
  labelKey = 'label',
  valueKey = 'value',
  placeholder = 'Select…',
  sortItems = true,
  searchable = true,
  disabled = false,
  clearable = false,
  onChange,
  onItemChange,
  onCancel,
  onTab,
  onOpen,
  onClose,
  defaultOpen = false,
}: DropdownlistProps) {
  const { theme } = useTheme();

  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const [activeFlatIndex, setActiveFlatIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(false);
  const [justOpened, setJustOpened] = useState(false);

  // Refs
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const activeRowRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollYRef = useRef(0);
  const lastTouchYRef = useRef<number | null>(null);
  const justOpenedRef = useRef(false);
  const openRef = useRef(false);

  // Keep refs in sync
  openRef.current = open;
  justOpenedRef.current = justOpened;

  // ---------- Computed values ----------

  const selectedItem = useMemo(() => {
    if (!items || items.length === 0) return null;
    for (let i = 0; i < items.length; i++) {
      if (items[i][valueKey] === value) return items[i];
    }
    return null;
  }, [items, valueKey, value]);

  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    const sorted = items.slice();
    if (sortItems) {
      sorted.sort((a, b) => {
        const la = String(a[labelKey] || '');
        const lb = String(b[labelKey] || '');
        return la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    return sorted;
  }, [items, sortItems, labelKey]);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return sortedItems;
    return sortedItems.filter((it) => {
      const lbl = String(it[labelKey] || '').toLowerCase();
      return lbl.indexOf(q) !== -1;
    });
  }, [sortedItems, query, labelKey]);

  const displayText = useMemo(
    () => (selectedItem ? selectedItem[labelKey] : ''),
    [selectedItem, labelKey]
  );

  const hasValue = !!displayText;

  // ---------- Mobile check ----------

  const checkMobile = useCallback(() => {
    setIsMobile((prev) => {
      const next = window.innerWidth <= 768;
      if (openRef.current && prev !== next) {
        // Close dropdown if switching between mobile/desktop while open
        setOpen(false);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768); // immediate initial check
    let timer: ReturnType<typeof setTimeout> | null = null;
    const debouncedCheckMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => checkMobile(), 150);
    };
    window.addEventListener('resize', debouncedCheckMobile);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', debouncedCheckMobile);
    };
  }, [checkMobile]);

  // ---------- Portal management (desktop only) ----------

  const cleanupPortal = useCallback(() => {
    if (portalContainerRef.current && portalContainerRef.current.parentNode === document.body) {
      document.body.removeChild(portalContainerRef.current);
    }
    portalContainerRef.current = null;
  }, []);

  const portalRafRef = useRef(0);
  const updatePortalPosition = useCallback(() => {
    if (!openRef.current || !portalContainerRef.current) return;

    // rAF-throttle: cancel any pending frame to avoid queuing multiples
    if (portalRafRef.current) cancelAnimationFrame(portalRafRef.current);
    portalRafRef.current = requestAnimationFrame(() => {
      portalRafRef.current = 0;
      const control = rootRef.current?.querySelector('.kst-select__control');
      const dropdown = portalContainerRef.current?.querySelector('.kst-select__dropdown');
      if (!control || !dropdown) return;

      const rect = control.getBoundingClientRect();
      const minWidth = 280;
      const dropdownWidth = Math.max(rect.width, minWidth);
      const dropdownHeight = (dropdown as HTMLElement).offsetHeight || 400;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let showAbove = false;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        showAbove = true;
      }

      const portal = portalContainerRef.current!;
      if (showAbove) {
        let topPosition = rect.top - dropdownHeight - 4;
        if (topPosition < 0) {
          topPosition = 4;
        }
        portal.style.top = topPosition + 'px';
        portal.classList.add('kst-select-portal--above');
      } else {
        portal.style.top = (rect.bottom + 4) + 'px';
        portal.classList.remove('kst-select-portal--above');
      }

      portal.style.left = rect.left + 'px';
      portal.style.width = dropdownWidth + 'px';
      portal.style.visibility = 'visible';
    });
  }, []);

  const ensurePortalContainer = useCallback(() => {
    if (!portalContainerRef.current) {
      const container = document.createElement('div');
      container.className = 'kst-select-portal' + (theme ? ' ' + theme : '');
      container.style.cssText = 'position: fixed; z-index: 9999; visibility: hidden;';

      const innerWrapper = document.createElement('div');
      innerWrapper.className = 'kst-select';
      container.appendChild(innerWrapper);
      document.body.appendChild(container);

      portalContainerRef.current = container;
    }
    return portalContainerRef.current;
  }, [theme]);

  // ---------- Scroll active item into view ----------

  const scrollActiveIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      const list = listRef.current;
      const active = activeRowRef.current;
      if (!list || !active) return;
      const aTop = active.offsetTop;
      const aBottom = aTop + active.offsetHeight;
      const vTop = list.scrollTop;
      const vBottom = vTop + list.clientHeight;
      if (aTop < vTop) list.scrollTop = aTop;
      else if (aBottom > vBottom) list.scrollTop = aBottom - list.clientHeight;
    });
  }, []);

  // ---------- Actions ----------

  const doClose = useCallback(() => {
    setOpen(false);
  }, []);

  const select = useCallback((item: DropdownlistItem) => {
    onChange?.(item[valueKey]);
    onItemChange?.(item);
    setOpen(false);
  }, [onChange, onItemChange, valueKey]);

  const clear = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onChange?.(null);
    onItemChange?.(null);
  }, [onChange, onItemChange]);

  // ---------- Keyboard ----------

  const onKeyDownControl = useCallback((e: React.KeyboardEvent) => {
    const k = e.key;
    if (!openRef.current && (k === 'ArrowDown' || k === 'Enter' || k === ' ')) {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  // We need filtered in the keydown handler but don't want stale closures,
  // so use a ref for filtered.
  const filteredRef = useRef(filtered);
  filteredRef.current = filtered;

  const activeFlatIndexRef = useRef(activeFlatIndex);
  activeFlatIndexRef.current = activeFlatIndex;

  const onKeyDownList = useCallback((e: React.KeyboardEvent) => {
    const k = e.key;
    const currentFiltered = filteredRef.current;
    const currentIdx = activeFlatIndexRef.current;

    if (k === 'Escape') {
      e.preventDefault();
      onCancel?.();
      setOpen(false);
      return;
    }

    if (k === 'Tab') {
      e.preventDefault();

      const item = currentFiltered[currentIdx];
      if (item) {
        onChange?.(item[valueKey]);
        onItemChange?.(item);
        setOpen(false);
      } else {
        onCancel?.();
        setOpen(false);
      }

      onTab?.({ shiftKey: e.shiftKey });
      return;
    }

    if (!currentFiltered.length) return;

    if (k === 'ArrowDown') {
      e.preventDefault();
      const nextIdx = Math.min(currentFiltered.length - 1, currentIdx + 1);
      scrollOnNextIndexChange.current = true;
      setActiveFlatIndex(nextIdx);
    } else if (k === 'ArrowUp') {
      e.preventDefault();
      const nextIdx = Math.max(0, currentIdx - 1);
      scrollOnNextIndexChange.current = true;
      setActiveFlatIndex(nextIdx);
    } else if (k === 'Home') {
      e.preventDefault();
      scrollOnNextIndexChange.current = true;
      setActiveFlatIndex(0);
    } else if (k === 'End') {
      e.preventDefault();
      scrollOnNextIndexChange.current = true;
      setActiveFlatIndex(currentFiltered.length - 1);
    } else if (k === 'Enter') {
      e.preventDefault();
      const item = currentFiltered[currentIdx];
      if (item) {
        onChange?.(item[valueKey]);
        onItemChange?.(item);
        setOpen(false);
      }
    }
  }, [onCancel, onChange, onItemChange, onTab, valueKey]);

  // Track whether the next activeFlatIndex change should scroll into view
  // (only for keyboard navigation, not mouse hover)
  const scrollOnNextIndexChange = useRef(false);

  useEffect(() => {
    if (open && scrollOnNextIndexChange.current) {
      scrollOnNextIndexChange.current = false;
      scrollActiveIntoView();
    }
  }, [activeFlatIndex, open, scrollActiveIntoView]);

  // ---------- Toggle open ----------

  const toggleOpen = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    if (!openRef.current) {
      // Emit global event to close datetime pickers when opening
      window.dispatchEvent(new CustomEvent('datetime-picker-opened', { detail: { source: 'kst-select' } }));
    }
    setOpen((prev) => !prev);
  }, [disabled]);

  // ---------- Click outside ----------

  const onClickOutside = useCallback((e: MouseEvent | TouchEvent) => {
    if (!openRef.current) return;
    if (justOpenedRef.current) return;

    const target = e.target as Node;
    const inComponent = rootRef.current?.contains(target);

    // isMobile might be stale in closure, read from DOM
    const mobile = window.innerWidth <= 768;

    if (mobile) {
      const modal = document.querySelector('.kst-select__modal');
      const inModal = modal?.contains(target);
      if (!inComponent && !inModal) {
        setOpen(false);
      }
    } else {
      const inPortal = portalContainerRef.current?.contains(target);
      if (!inComponent && !inPortal) {
        setOpen(false);
      }
    }
  }, []);

  // ---------- Touch scroll chaining prevention ----------

  const resetTouchTracking = useCallback(() => {
    lastTouchYRef.current = null;
  }, []);

  const preventScrollChaining = useCallback((e: React.TouchEvent) => {
    const list = listRef.current;
    if (!list) return;

    const scrollTop = list.scrollTop;
    const scrollHeight = list.scrollHeight;
    const clientHeight = list.clientHeight;

    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight;

    const touchY = e.touches[0].clientY;
    if (lastTouchYRef.current === null) {
      lastTouchYRef.current = touchY;
      return;
    }

    const deltaY = lastTouchYRef.current - touchY;
    const isScrollingUp = deltaY < 0;
    const isScrollingDown = deltaY > 0;

    if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
      e.preventDefault();
    }

    lastTouchYRef.current = touchY;
  }, []);

  // ---------- Backdrop click ----------

  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('kst-select__modal-backdrop')) {
      setOpen(false);
    }
  }, []);

  // ---------- Effect: open/close lifecycle ----------

  useEffect(() => {
    if (open) {
      onOpen?.();
      setJustOpened(true);
      justOpenedRef.current = true;

      // Set active index to selected item or first item
      let idx = -1;
      for (let i = 0; i < filtered.length; i++) {
        if (filtered[i][valueKey] === value) { idx = i; break; }
      }
      scrollOnNextIndexChange.current = true;
      setActiveFlatIndex(idx >= 0 ? idx : (filtered.length ? 0 : -1));

      if (!isMobile) {
        // Desktop: create portal, position, and focus
        ensurePortalContainer();

        // Wait for DOM then position and focus
        requestAnimationFrame(() => {
          updatePortalPosition();
          requestAnimationFrame(() => {
            if (searchable) {
              searchRef.current?.focus();
            } else {
              listRef.current?.focus();
            }
          });
        });

        window.addEventListener('scroll', updatePortalPosition, true);
        window.addEventListener('resize', updatePortalPosition);
      }

      // Lock body scroll on mobile
      if (isMobile) {
        scrollYRef.current = window.scrollY || window.pageYOffset;
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + scrollYRef.current + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
      }

      // Clear justOpened flag after delay
      const timer = setTimeout(() => {
        setJustOpened(false);
        justOpenedRef.current = false;
      }, 200);

      return () => clearTimeout(timer);
    } else {
      onClose?.();
      setQuery('');
      setActiveFlatIndex(-1);
      setJustOpened(false);
      justOpenedRef.current = false;

      if (!isMobile) {
        // Cleanup portal on next frame
        requestAnimationFrame(() => {
          cleanupPortal();
        });
        window.removeEventListener('scroll', updatePortalPosition, true);
        window.removeEventListener('resize', updatePortalPosition);
      }

      // Restore body scroll on mobile
      if (isMobile) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollYRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---------- Effect: register click-outside listeners on mount ----------

  useEffect(() => {
    const timer = setTimeout(() => {
      document.addEventListener('mouseup', onClickOutside as EventListener, true);
      document.addEventListener('touchend', onClickOutside as EventListener, { passive: true, capture: true });
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseup', onClickOutside as EventListener, true);
      document.removeEventListener('touchend', onClickOutside as EventListener, true);
    };
  }, [onClickOutside]);

  // ---------- Effect: cleanup on unmount ----------

  useEffect(() => {
    return () => {
      cleanupPortal();
      window.removeEventListener('scroll', updatePortalPosition, true);
      window.removeEventListener('resize', updatePortalPosition);

      // Ensure body scroll is restored
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Effect: update portal theme class ----------

  useEffect(() => {
    if (portalContainerRef.current) {
      portalContainerRef.current.classList.remove('theme-gray-dark', 'theme-blue-dark');
      if (theme) {
        portalContainerRef.current.classList.add(theme);
      }
    }
  }, [theme]);

  // ---------- Search input handler ----------

  const onSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    // Reset active index when search changes
    // We use a callback so we read the freshest filtered value after query updates
    // But since filtered is derived from query, we need to defer:
    // Actually, the activeFlatIndex will be set after re-render via the next render cycle.
    // For immediate feedback, we compute inline.
    const q = val.trim().toLowerCase();
    let newFiltered: DropdownlistItem[];
    if (!q) {
      newFiltered = sortedItems;
    } else {
      newFiltered = sortedItems.filter((it) => {
        const lbl = String(it[labelKey] || '').toLowerCase();
        return lbl.indexOf(q) !== -1;
      });
    }
    scrollOnNextIndexChange.current = true;
    setActiveFlatIndex(newFiltered.length ? 0 : -1);
  }, [sortedItems, labelKey]);

  // ---------- Render list content ----------

  const renderListContent = () => {
    if (!filtered.length) {
      return (
        <div className="kst-select__item kst-select__item--empty" aria-disabled="true">
          No results
        </div>
      );
    }

    return filtered.map((item, idx) => {
      const isSel = value === item[valueKey];
      const isAct = idx === activeFlatIndex;
      return (
        <div
          key={item[valueKey] ?? idx}
          className={`kst-select__item${isAct ? ' kst-select__item--active' : ''}`}
          aria-selected={isSel ? 'true' : 'false'}
          role="option"
          ref={isAct ? activeRowRef : undefined}
          onClick={() => select(item)}
          onMouseEnter={() => { if (!justOpenedRef.current) setActiveFlatIndex(idx); }}
        >
          {item[labelKey]}
        </div>
      );
    });
  };

  // ---------- Desktop dropdown (rendered into portal) ----------

  const desktopDropdown = !isMobile && open ? (
    <div className="kst-select__dropdown" role="listbox">
      {searchable && (
        <div className="kst-select__search">
          <input
            ref={searchRef}
            className="kst-select__search-input"
            value={query}
            type="text"
            placeholder="Search…"
            aria-label="Filter options"
            onChange={onSearchInput}
            onKeyDown={onKeyDownList}
          />
        </div>
      )}
      <div
        className="kst-select__list"
        ref={listRef}
        tabIndex={searchable ? undefined : 0}
        onKeyDown={onKeyDownList}
      >
        {renderListContent()}
      </div>
    </div>
  ) : null;

  // Render the desktop dropdown into the portal container.
  // If the portal isn't ready yet, render nothing (it will appear on the
  // next frame once useEffect creates the container).  Rendering inline
  // as a fallback caused the form to briefly expand and flash.
  const desktopPortal = desktopDropdown && portalContainerRef.current
    ? createPortal(
        desktopDropdown,
        portalContainerRef.current.querySelector('.kst-select') || portalContainerRef.current
      )
    : null;

  // ---------- Mobile modal ----------

  const mobileModal = isMobile && open ? (
    <div className="kst-select__modal-backdrop" onClick={onBackdropClick}>
      <div className="kst-select__modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="kst-select__modal-header">
          <div className="kst-select__modal-title">{placeholder || 'Select'}</div>
          <button
            className="kst-select__modal-close"
            type="button"
            aria-label="Close"
            onClick={doClose}
          >
            &times;
          </button>
        </div>
        {/* Modal Search */}
        {searchable && (
          <div className="kst-select__modal-search">
            <input
              ref={searchRef}
              className="kst-select__modal-search-input"
              value={query}
              type="text"
              placeholder="Search…"
              aria-label="Filter options"
              inputMode="text"
              onChange={onSearchInput}
              onKeyDown={onKeyDownList}
            />
          </div>
        )}
        {/* Modal List */}
        <div
          className="kst-select__modal-list"
          ref={listRef}
          onTouchStart={resetTouchTracking}
          onTouchMove={preventScrollChaining}
          onTouchEnd={resetTouchTracking}
        >
          {renderListContent()}
        </div>
      </div>
    </div>
  ) : null;

  // ---------- Control classes ----------

  const controlClasses = [
    'kst-select__control',
    open ? 'kst-select__control--open' : '',
    disabled ? 'kst-select__control--disabled' : '',
  ].filter(Boolean).join(' ');

  // ---------- Main render ----------

  return (
    <div className={`kst-select${theme ? ' ' + theme : ''}`} ref={rootRef}>
      <div
        className={controlClasses}
        role="combobox"
        aria-expanded={open ? 'true' : 'false'}
        aria-disabled={disabled ? 'true' : 'false'}
        tabIndex={disabled ? -1 : 0}
        onClick={(e) => {
          e.stopPropagation();
          toggleOpen(e);
        }}
        onKeyDown={onKeyDownControl}
      >
        <div className={`kst-select__value${!hasValue ? ' kst-select__value--placeholder' : ''}`}>
          {hasValue ? displayText : placeholder}
        </div>
        {clearable && hasValue && !disabled && (
          <div
            className="kst-select__clear"
            aria-label="Clear selection"
            onClick={clear}
          >
            &times;
          </div>
        )}
        <div className="kst-select__caret" />
      </div>
      {desktopPortal}
      {mobileModal}
    </div>
  );
});

(window as any).KstSelectReact = Dropdownlist;
